import React, { useState, useEffect, useCallback } from 'react';
import { 
  format, 
  startOfDay, 
  endOfDay, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth,
  addDays,
  eachDayOfInterval,
  isSameDay,
  isToday
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNavigate } from 'react-router-dom';
import LoadingState from '../shared/LoadingState';
import './Agenda.css';

const Agenda = () => {
  const { userData } = useAuth();

  // Helper para pegar companyId correto (colaborador usa currentCompanyId)
  const getCompanyId = useCallback(() => {
    return userData?.role === 'collaborator' 
      ? userData?.currentCompanyId 
      : userData?.companyId;
  }, [userData]);
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewPeriod, setViewPeriod] = useState('day'); // 'day', 'week', 'month'
  const [loading, setLoading] = useState(true);
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDayReservations, setSelectedDayReservations] = useState([]);
  const [selectedModalDate, setSelectedModalDate] = useState(null);

  const loadReservations = useCallback(async () => {
    try {
      setLoading(true);
      const companyId = getCompanyId();

      let startDate, endDate;

      if (viewPeriod === 'day') {
        startDate = startOfDay(selectedDate);
        endDate = endOfDay(selectedDate);
      } else if (viewPeriod === 'week') {
        startDate = startOfWeek(selectedDate, { locale: ptBR });
        endDate = endOfWeek(selectedDate, { locale: ptBR });
      } else if (viewPeriod === 'month') {
        startDate = startOfMonth(selectedDate);
        endDate = endOfMonth(selectedDate);
      }

      const reservationsRef = collection(db, `companies/${companyId}/reservations`);

      // Se for colaborador, limitar a reservas criadas por ele (reduz exposição de dados)
      let reservationsQuery;
      if (userData?.role === 'collaborator' && userData?.email) {
        reservationsQuery = query(
          reservationsRef,
          where('createdByEmail', '==', userData.email),
          where('date', '>=', startDate),
          where('date', '<=', endDate),
          orderBy('date', 'asc')
        );
      } else {
        reservationsQuery = query(
          reservationsRef,
          where('date', '>=', startDate),
          where('date', '<=', endDate),
          orderBy('date', 'asc')
        );
      }

      const snapshot = await getDocs(reservationsQuery);
      let data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      if (userData?.role === 'collaborator') {
        data = data.filter(reservation => ['pending', 'confirmed'].includes(reservation.status));
      }

      // Expandir reservas de pousada para todos os dias do período
      const expandedReservations = [];
      data.forEach(reservation => {
        if (reservation.checkInDate && reservation.checkOutDate) {
          const checkIn = reservation.checkInDate.toDate();
          const checkOut = reservation.checkOutDate.toDate();
          const daysInPeriod = eachDayOfInterval({
            start: checkIn,
            end: addDays(checkOut, -1)
          });

          daysInPeriod.forEach((day, index) => {
            expandedReservations.push({
              ...reservation,
              date: { toDate: () => day },
              _isMultiDay: true,
              _dayIndex: index + 1,
              _totalDays: reservation.dailyCount || daysInPeriod.length
            });
          });
        } else {
          expandedReservations.push(reservation);
        }
      });

      setReservations(expandedReservations);
    } catch (error) {
      console.error('Erro ao carregar reservas:', error);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [getCompanyId, selectedDate, viewPeriod, userData]);

  useEffect(() => {
    if (getCompanyId()) {
      loadReservations();
    }
  }, [getCompanyId, loadReservations]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#4caf50';
      case 'pending': return '#ff9800';
      case 'cancelled': return '#f44336';
      case 'no_show': return '#64748b';
      case 'completed': return '#2196f3';
      default: return '#999';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'HH:mm');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'dd/MM');
  };

  const formatCreatedBy = (createdBy, createdByEmail) => {
    if (!createdBy && !createdByEmail) return 'Sistema';
    
    // Se tiver nome, mostra nome (email)
    if (createdBy && createdBy !== createdByEmail) {
      return `${createdBy}${createdByEmail ? ` (${createdByEmail})` : ''}`;
    }
    
    // Se só tiver email, extrai nome do email
    if (createdByEmail) {
      const nameFromEmail = createdByEmail.split('@')[0];
      return `${nameFromEmail} (${createdByEmail})`;
    }
    
    return createdBy || 'Sistema';
  };

  const navigateDate = (direction) => {
    const daysToAdd = viewPeriod === 'day' ? 1 : viewPeriod === 'week' ? 7 : 30;
    const newDate = addDays(selectedDate, direction === 'next' ? daysToAdd : -daysToAdd);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const getReservationsForDay = (day) => {
    return reservations.filter(res => {
      const resDate = res.date.toDate ? res.date.toDate() : new Date(res.date);
      return isSameDay(resDate, day);
    });
  };

  const getPeriodTitle = () => {
    if (viewPeriod === 'day') {
      return format(selectedDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } else if (viewPeriod === 'week') {
      const start = startOfWeek(selectedDate, { locale: ptBR });
      const end = endOfWeek(selectedDate, { locale: ptBR });
      return `${format(start, 'dd MMM', { locale: ptBR })} - ${format(end, 'dd MMM yyyy', { locale: ptBR })}`;
    } else {
      return format(selectedDate, "MMMM 'de' yyyy", { locale: ptBR });
    }
  };

  const openReservationDetails = (reservation) => {
    if (!reservation?.id) return;
    setShowDayModal(false);
    navigate(`/reservations?reservation=${encodeURIComponent(reservation.id)}`);
  };

  const renderDayView = () => {
    if (reservations.length === 0) {
      return (
        <div className="empty-state">
          <CalendarIcon size={48} color="#ccc" />
          <p>Nenhuma reserva para este dia</p>
          <button className="btn-secondary" onClick={() => navigate('/reservations')}>
            Criar Nova Reserva
          </button>
        </div>
      );
    }

    return (
      <div className="reservation-cards">
        {reservations.map((reservation) => (
          <button
            key={`${reservation.id}-${reservation._dayIndex || 0}`}
            type="button"
            className="reservation-card reservation-card--clickable"
            onClick={() => openReservationDetails(reservation)}
          >
            <div
              className="status-indicator"
              style={{ backgroundColor: getStatusColor(reservation.status) }}
            />
            <div className="reservation-content">
              <div className="reservation-time">
                <CalendarIcon size={16} />
                {reservation._isMultiDay ? (
                  <span>
                    Dia {reservation._dayIndex}/{reservation._totalDays}
                    {reservation.roomName && ` - ${reservation.roomName}`}
                  </span>
                ) : (
                  formatTime(reservation.date)
                )}
              </div>
              <div className="reservation-info">
                <h3>{reservation.serviceName}</h3>
                <p className="client-name">{reservation.clientName}</p>
                {reservation.participants && (
                  <p className="participants">{reservation.participants} participante(s)</p>
                )}
                {reservation._isMultiDay && (
                  <p className="multi-day-info" style={{ color: '#667eea', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    📅 Hospedagem: {reservation.checkInDate?.toDate().toLocaleDateString('pt-BR')} - {reservation.checkOutDate?.toDate().toLocaleDateString('pt-BR')}
                  </p>
                )}
                {(reservation.createdBy || reservation.createdByEmail) && (
                  <p className="created-by">
                    <User size={14} />
                    Cadastrado por: {formatCreatedBy(reservation.createdBy, reservation.createdByEmail)}
                  </p>
                )}
              </div>
              <div className="reservation-footer">
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(reservation.status) }}
                >
                  {reservation.status === 'confirmed' ? 'Confirmada' :
                   reservation.status === 'pending' ? 'Pendente' :
                   reservation.status === 'cancelled' ? 'Cancelada' :
                   reservation.status === 'completed' ? 'Concluída' :
                   reservation.status === 'no_show' ? 'Não compareceu' : reservation.status}
                </span>
                {reservation.totalAmount && (
                  <span className="amount">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                      .format(reservation.totalAmount)}
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(selectedDate, { locale: ptBR });
    const weekEnd = endOfWeek(selectedDate, { locale: ptBR });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    return (
      <div className="week-view">
        {days.map(day => {
          const dayReservations = getReservationsForDay(day);
          const isCurrentDay = isToday(day);
          
          return (
            <div key={day.toString()} className={`week-day ${isCurrentDay ? 'today' : ''}`}>
              <div className="week-day-header">
                <h4>{format(day, 'EEEE', { locale: ptBR })}</h4>
                <span className="day-number">{format(day, 'dd')}</span>
              </div>
              <div className="week-day-content">
                {dayReservations.length === 0 ? (
                  <p className="no-reservations">Sem reservas</p>
                ) : (
                  dayReservations.map(res => (
	                    <div
	                      key={res.id}
	                      className="week-reservation week-reservation--clickable"
	                      style={{ borderLeftColor: getStatusColor(res.status) }}
                        onClick={() => openReservationDetails(res)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openReservationDetails(res);
                          }
                        }}
	                    >
                      <div className="week-res-time">{formatTime(res.date)}</div>
                      <div className="week-res-service">{res.serviceName}</div>
                      <div className="week-res-client">{res.clientName}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

    const handleDayClick = (day) => {
      const dayReservations = getReservationsForDay(day);
      if (dayReservations.length > 0) {
        setSelectedDayReservations(dayReservations);
        setSelectedModalDate(day);
        setShowDayModal(true);
      }
    };

    return (
      <div className="month-view">
        {days.map(day => {
          const dayReservations = getReservationsForDay(day);
          const isCurrentDay = isToday(day);
          
          return (
            <div 
              key={day.toString()} 
              className={`month-day ${isCurrentDay ? 'today' : ''} ${dayReservations.length > 0 ? 'has-reservations' : ''}`}
              onClick={() => handleDayClick(day)}
            >
              <div className="month-day-number">{format(day, 'd')}</div>
              {dayReservations.length > 0 && (
                <div className="month-day-indicator">
                  {dayReservations.length}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) return (
    <div className="agenda-container">
      <LoadingState title="Carregando agenda" subtitle="Montando sua visualizacao de reservas" />
    </div>
  );

  return (
    <div className="agenda-container">
      <div className="page-header">
        <div>
          <h1>Agenda</h1>
          <p className="period-title">{getPeriodTitle()}</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/reservations')}>
          <Plus size={20} />
          Nova Reserva
        </button>
      </div>

      <div className="agenda-controls">
        <div className="period-selector">
          <button
            className={viewPeriod === 'day' ? 'active' : ''}
            onClick={() => setViewPeriod('day')}
          >
            Dia
          </button>
          <button
            className={viewPeriod === 'week' ? 'active' : ''}
            onClick={() => setViewPeriod('week')}
          >
            Semana
          </button>
          <button
            className={viewPeriod === 'month' ? 'active' : ''}
            onClick={() => setViewPeriod('month')}
          >
            Mês
          </button>
        </div>

        <div className="date-navigation">
          <button onClick={() => navigateDate('prev')} title="Anterior">
            <ChevronLeft size={20} />
          </button>
          <button className="btn-today" onClick={goToToday}>
            Hoje
          </button>
          <button onClick={() => navigateDate('next')} title="Próximo">
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div className="agenda-content">
        {viewPeriod === 'day' && renderDayView()}
        {viewPeriod === 'week' && renderWeekView()}
        {viewPeriod === 'month' && renderMonthView()}
      </div>

      {/* Modal de Reservas do Dia */}
      {showDayModal && (
        <div className="modal-overlay" onClick={() => setShowDayModal(false)}>
          <div className="modal-content day-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                Reservas de {selectedModalDate && format(selectedModalDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </h2>
              <button className="modal-close" onClick={() => setShowDayModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {selectedDayReservations.length === 0 ? (
                <p className="no-reservations">Nenhuma reserva neste dia</p>
              ) : (
                <div className="reservation-cards">
                  {selectedDayReservations.map((reservation) => (
	                    <button
                        key={reservation.id}
                        type="button"
                        className="reservation-card reservation-card--clickable"
                        onClick={() => openReservationDetails(reservation)}
                      >
                      <div
                        className="status-indicator"
                        style={{ backgroundColor: getStatusColor(reservation.status) }}
                      />
                      <div className="reservation-content">
                        <div className="reservation-time">
                          <CalendarIcon size={16} />
                          {formatTime(reservation.date)}
                        </div>
                        <div className="reservation-info">
                          <h3>{reservation.serviceName}</h3>
                          <p className="client-name">{reservation.clientName}</p>
                          {reservation.participants && (
                            <p className="participants">{reservation.participants} participante(s)</p>
                          )}
                          {(reservation.createdBy || reservation.createdByEmail) && (
                            <p className="created-by">
                              <User size={14} />
                              Cadastrado por: {formatCreatedBy(reservation.createdBy, reservation.createdByEmail)}
                            </p>
                          )}
                        </div>
                        <div className="reservation-footer">
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(reservation.status) }}
                          >
                            {reservation.status === 'confirmed' ? 'Confirmada' :
                             reservation.status === 'pending' ? 'Pendente' :
                             reservation.status === 'cancelled' ? 'Cancelada' :
                             reservation.status === 'completed' ? 'Concluída' :
                             reservation.status === 'no_show' ? 'Não compareceu' : reservation.status}
                          </span>
                          {reservation.totalAmount && (
                            <span className="amount">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
                                .format(reservation.totalAmount)}
                            </span>
                          )}
                        </div>
                      </div>
	                    </button>
	                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDayModal(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agenda;
